import './style.css';
import config from './data/config.json';
import routeData from './data/route.json';
import { ALL_PROPERTIES, BY_ID, formatPrice, formatTimeRange, formatTime } from './properties';
import type { RouteDay, RouteStop } from './types';

declare const L: any;

interface Prefs {
  excluded: string[];
  satStart: string;
  sunStart: string;
  viewingMinutes: number;
}

let prefs: Prefs = { excluded: [], satStart: '11:30', sunStart: '11:30', viewingMinutes: 18 };
let activeDay: 'saturday' | 'sunday' = 'sunday';
let map: any = null;
let markerLayer: any = null;
let lineLayer: any = null;
let saveTimer: number | null = null;

document.getElementById('sub')!.textContent = `Starting from ${config.start.label} · plan curated by hand`;

async function loadPrefs() {
  try {
    const res = await fetch('/api/prefs');
    if (res.ok) prefs = await res.json();
  } catch {}
}

function schedulePrefSave() {
  if (saveTimer) clearTimeout(saveTimer);
  const status = document.getElementById('prefs-status')!;
  status.textContent = 'Saving…'; status.className = 'prefs-status';
  saveTimer = window.setTimeout(savePrefs, 400);
}

async function savePrefs() {
  const status = document.getElementById('prefs-status')!;
  try {
    const res = await fetch('/api/prefs', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(prefs)
    });
    if (!res.ok) throw new Error(`${res.status}`);
    prefs = await res.json();
    status.textContent = 'Saved'; status.className = 'prefs-status saved';
  } catch (e) {
    status.textContent = `Save failed: ${(e as Error).message}`; status.className = 'prefs-status error';
  }
}

const tabs = document.querySelectorAll<HTMLButtonElement>('.day-tabs button');
tabs.forEach((tab) => {
  tab.addEventListener('click', () => {
    tabs.forEach((t) => t.classList.remove('active'));
    tab.classList.add('active');
    activeDay = tab.dataset.day as 'saturday' | 'sunday';
    rerender();
  });
});

function ensureMap() {
  if (map) return;
  map = L.map(document.getElementById('map')!, { scrollWheelZoom: false }).setView([37.83, -122.30], 11);
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(map);
  map.createPane('routeline');
  map.getPane('routeline').style.zIndex = '450';
  document.getElementById('toggle-line')!.addEventListener('change', (e) => {
    if (!lineLayer) return;
    if ((e.target as HTMLInputElement).checked) lineLayer.addTo(map); else map.removeLayer(lineLayer);
  });
  document.getElementById('toggle-markers')!.addEventListener('change', (e) => {
    if (!markerLayer) return;
    if ((e.target as HTMLInputElement).checked) markerLayer.addTo(map); else map.removeLayer(markerLayer);
  });
}

function activeStops(day: RouteDay): RouteStop[] {
  // Filter out visit stops whose property is excluded. Drop the immediately-preceding drive
  // segment too so we don't end up with orphan drive lines.
  const out: RouteStop[] = [];
  for (let i = 0; i < day.stops.length; i++) {
    const s = day.stops[i];
    if (s.kind === 'visit' && prefs.excluded.includes(s.propertyId)) {
      while (out.length && out[out.length - 1].kind === 'drive') out.pop();
      continue;
    }
    out.push(s);
  }
  return out;
}

function renderMap(stops: RouteStop[]) {
  ensureMap();
  if (markerLayer) { map.removeLayer(markerLayer); markerLayer = null; }
  if (lineLayer)   { map.removeLayer(lineLayer);   lineLayer = null; }

  const markers: any[] = [];
  const points: [number, number][] = [];

  markers.push(L.marker([config.start.lat, config.start.lon], {
    icon: L.divIcon({ className: 'stop-marker stop-marker-start', html: `<div class="pin start">SF</div>`, iconSize: [32, 32], iconAnchor: [16, 16] })
  }).bindPopup(`<b>Start</b><br>${config.start.label}`));
  points.push([config.start.lat, config.start.lon]);

  let n = 0;
  for (const stop of stops) {
    if (stop.kind !== 'visit') continue;
    n += 1;
    const p = BY_ID[stop.propertyId];
    if (!p) continue;
    points.push([p.lat, p.lon]);
    markers.push(L.marker([p.lat, p.lon], {
      icon: L.divIcon({ className: 'stop-marker', html: `<div class="pin">${n}</div>`, iconSize: [32, 32], iconAnchor: [16, 16] })
    }).bindPopup(`<b>${n}. ${escape(p.shortName)}</b><br>${formatPrice(p.price) || '$?'} · ${stop.arrive} → ${stop.depart}<br><a href="/property/${p.id}.html">Details →</a>`));
  }

  markerLayer = L.layerGroup(markers);
  if ((document.getElementById('toggle-markers') as HTMLInputElement).checked) markerLayer.addTo(map);

  if (points.length > 1) {
    const halo = L.polyline(points, { pane: 'routeline', color: '#ffffff', weight: 8, opacity: 0.9, lineCap: 'round', lineJoin: 'round' });
    const main = L.polyline(points, { pane: 'routeline', color: '#d63b3b', weight: 4, opacity: 1, lineCap: 'round', lineJoin: 'round' });
    lineLayer = L.layerGroup([halo, main]);
    if ((document.getElementById('toggle-line') as HTMLInputElement).checked) lineLayer.addTo(map);
    map.fitBounds(L.polyline(points).getBounds(), { padding: [40, 40] });
  }
}

let visitCounter = 0;
function renderStop(stop: RouteStop): string {
  if (stop.kind === 'depart') {
    visitCounter = 0;
    return `<div class="stop depart"><div class="when">${formatTime(stop.time)}</div><div class="body"><div class="title">${escape(stop.label)}</div><div class="meta">${escape(stop.address)}</div></div></div>`;
  }
  if (stop.kind === 'drive') {
    return `<div class="stop drive">${stop.minutes} min · ${escape(stop.leg)}</div>`;
  }
  visitCounter += 1;
  const p = BY_ID[stop.propertyId];
  if (!p) return '';
  const cover = p.photos?.[0] ? `<img class="stop-cover" src="${p.photos[0]}" alt="${escape(p.shortName)}" loading="lazy" />` : '';
  const priceTxt = p.price > 0 ? formatPrice(p.price) : '';
  const specsTxt = p.beds > 0 ? `${p.beds}bd / ${p.baths}ba / ${p.sqft.toLocaleString()} sqft` : '<span style="opacity:0.5">specs pending</span>';
  return `<div class="stop"><div class="when"><span class="num">${visitCounter}</span><br/>${formatTime(stop.arrive)}<br/><span style="opacity:0.6">→ ${formatTime(stop.depart)}</span></div><div class="body">${cover}<div class="title"><a class="address" href="/property/${p.id}.html">${escape(p.shortName)}</a> ${priceTxt ? `· <span class="price">${priceTxt}</span>` : ''}</div><div class="meta">${escape(p.neighborhood)}, ${escape(p.city)} · ${specsTxt}</div>${stop.note ? `<div class="note">${escape(stop.note)}</div>` : ''}</div></div>`;
}

function rerender() {
  const day: RouteDay = (routeData as any)[activeDay];
  const stops = activeStops(day);
  const visits = stops.filter((s) => s.kind === 'visit').length;

  document.getElementById('rationale')!.innerHTML = `<strong>Plan:</strong> ${visits} stop${visits !== 1 ? 's' : ''} on ${activeDay === 'saturday' ? 'Saturday May 2' : 'Sunday May 3'} · ${escape(day.rationale)}`;

  if (typeof L !== 'undefined') renderMap(stops);
  document.getElementById('schedule')!.innerHTML = stops.map(renderStop).join('');

  // Available-but-not-routed: properties that ARE open today/tomorrow (depending on tab)
  // but are not in the route plan, AND not explicitly excluded.
  const dateKey = activeDay === 'saturday' ? config.today : config.tomorrow;
  const visitedIds = new Set(day.stops.filter((s) => s.kind === 'visit').map((s: any) => s.propertyId));
  const available = ALL_PROPERTIES.filter((p) =>
    !visitedIds.has(p.id) &&
    !prefs.excluded.includes(p.id) &&
    p.openHouses.some((w) => w.date === dateKey)
  );

  const skipEl = document.getElementById('skipped')!;
  if (!available.length) {
    skipEl.innerHTML = '';
  } else {
    skipEl.innerHTML = `<div class="skipped">
      <h3>Open today but not on this route (${available.length})</h3>
      <ul>${available.map((p) => {
        const w = p.openHouses.find((x) => x.date === dateKey)!;
        const price = p.price > 0 ? formatPrice(p.price) : '';
        return `<li><a href="/property/${p.id}.html"><strong>${escape(p.shortName)}</strong></a> · ${escape(p.neighborhood)} · ${formatTimeRange(w.start, w.end)}${price ? ` · ${price}` : ''}</li>`;
      }).join('')}</ul>
      <p style="margin-top:8px;color:var(--muted);font-size:12px">If you want any of these in the route, message Claude and I'll re-thread the plan around them.</p>
    </div>`;
  }

  renderPicker();
}

function renderPicker() {
  const grid = document.getElementById('picker-grid')!;
  const today = config.today;
  const tomorrow = config.tomorrow;
  const sorted = ALL_PROPERTIES.slice().sort((a, b) => {
    const ao = (a.openHouses.find((w) => w.date === today) ? 0 : 1);
    const bo = (b.openHouses.find((w) => w.date === today) ? 0 : 1);
    if (ao !== bo) return ao - bo;
    return (a.price || 1e9) - (b.price || 1e9);
  });

  grid.innerHTML = sorted.map((p) => {
    const sat = p.openHouses.find((w) => w.date === today);
    const sun = p.openHouses.find((w) => w.date === tomorrow);
    const oh = sat ? `Sat ${formatTimeRange(sat.start, sat.end)}` : sun ? `Sun ${formatTimeRange(sun.start, sun.end)}` : 'not open';
    const cover = p.photos?.[0] ? `<img src="${p.photos[0]}" alt="" loading="lazy" />` : `<div class="no-photo">no photo yet</div>`;
    const checked = !prefs.excluded.includes(p.id) ? 'checked' : '';
    const priceTxt = p.price > 0 ? formatPrice(p.price) : '<span style="opacity:0.5">$?</span>';
    return `<label class="picker-card ${checked ? 'on' : 'off'}">
      <input type="checkbox" data-id="${p.id}" ${checked} />
      ${cover}
      <div class="picker-meta">
        <div class="picker-row1"><span class="price">${priceTxt}</span><span class="oh ${sat ? '' : 'sun'}">${escape(oh)}</span></div>
        <div class="picker-addr">${escape(p.shortName)}</div>
        <div class="picker-loc">${escape(p.city)}</div>
      </div>
    </label>`;
  }).join('');

  grid.querySelectorAll<HTMLInputElement>('input[type=checkbox]').forEach((cb) => {
    cb.addEventListener('change', () => {
      const id = cb.dataset.id!;
      if (cb.checked) prefs.excluded = prefs.excluded.filter((x) => x !== id);
      else if (!prefs.excluded.includes(id)) prefs.excluded.push(id);
      schedulePrefSave();
      rerender();
    });
  });

  const total = ALL_PROPERTIES.length;
  const selected = total - prefs.excluded.length;
  document.getElementById('picker-count')!.textContent = `(${selected} of ${total} included)`;
}

document.getElementById('select-all')!.addEventListener('click', () => { prefs.excluded = []; schedulePrefSave(); rerender(); });
document.getElementById('select-none')!.addEventListener('click', () => { prefs.excluded = ALL_PROPERTIES.map((p) => p.id); schedulePrefSave(); rerender(); });

// Sat/Sun start + viewing min are kept in prefs but no longer drive the schedule.
// Hide them — manual route is fixed.
const ctrls = document.querySelector('.picker-controls');
if (ctrls) {
  ctrls.querySelectorAll('label').forEach((l) => l.remove());
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

(async () => {
  await loadPrefs();
  rerender();
})();
